import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { subscribeToAllOrders, type Order } from '../../../utils/firebase';
import { Search, Filter, ChevronDown, ArrowUpDown } from 'lucide-react';
function formatPrice(cents: number): string {
  return `$${(cents / 100).toFixed(2)}`;
}
function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric'
  });
}
export function AdminDashboard() {
  const [orders, setOrders] = useState<Order[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [typeFilter, setTypeFilter] = useState<string>('all');
  const [sortConfig, setSortConfig] = useState<{
    key: keyof Order | 'date';
    direction: 'asc' | 'desc';
  }>({
    key: 'date',
    direction: 'desc'
  });
  useEffect(() => {
    const unsub = subscribeToAllOrders(data => {
      setOrders(data);
      setLoading(false);
    });
    return unsub;
  }, []);
  const stats = useMemo(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    let totalRevenue = 0;
    let revenueToday = 0;
    let ordersToday = 0;
    let pending = 0;
    let shipped = 0;
    let digital = 0;
    let physical = 0;
    orders.forEach(o => {
      if (o.status === 'paid') {
        totalRevenue += o.total;
        const orderDate = new Date(o.createdAt);
        if (orderDate >= today) {
          revenueToday += o.total;
          ordersToday++;
        }
      }
      if (o.fulfillmentStatus === 'pending') pending++;
      if (o.fulfillmentStatus === 'shipped') shipped++;
      const hasDigital = o.lineItems.some(i => i.type === 'book');
      const hasPhysical = o.lineItems.some(i => i.type === 'supplement');
      if (hasDigital) digital++;
      if (hasPhysical) physical++;
    });
    return {
      totalOrders: orders.length,
      ordersToday,
      revenueToday,
      totalRevenue,
      pending,
      shipped,
      digital,
      physical
    };
  }, [orders]);
  const filteredOrders = useMemo(() => {
    return orders.filter(o => {
      const matchesSearch = o.id.toLowerCase().includes(search.toLowerCase()) || o.email.toLowerCase().includes(search.toLowerCase()) || (o.shippingAddress?.recipientName || '').toLowerCase().includes(search.toLowerCase());
      const matchesStatus = statusFilter === 'all' || o.fulfillmentStatus === statusFilter;
      const hasDigital = o.lineItems.some(i => i.type === 'book');
      const hasPhysical = o.lineItems.some(i => i.type === 'supplement');
      const matchesType = typeFilter === 'all' || typeFilter === 'digital' && hasDigital || typeFilter === 'physical' && hasPhysical;
      return matchesSearch && matchesStatus && matchesType;
    }).sort((a, b) => {
      if (sortConfig.key === 'date') {
        const dateA = new Date(a.createdAt).getTime();
        const dateB = new Date(b.createdAt).getTime();
        return sortConfig.direction === 'asc' ? dateA - dateB : dateB - dateA;
      }
      // Add other sort logic if needed
      return 0;
    });
  }, [orders, search, statusFilter, typeFilter, sortConfig]);
  const handleSort = (key: typeof sortConfig.key) => {
    setSortConfig(current => ({
      key,
      direction: current.key === key && current.direction === 'desc' ? 'asc' : 'desc'
    }));
  };
  if (loading) {
    return <div className="p-8 flex justify-center" data-id="element-155">
        <span className="w-6 h-6 border-2 border-tealslate/30 border-t-tealslate rounded-full animate-spin" data-id="element-156" />
      </div>;
  }
  return <div className="p-8 max-w-7xl mx-auto" data-id="element-157">
      <h1 className="text-2xl font-bold text-[#111111] mb-8" data-id="element-158">
        Dashboard Overview
      </h1>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-10" data-id="element-159">
        <div className="bg-white p-6 rounded-xl border border-[#E5E5E5] shadow-sm" data-id="element-160">
          <p className="text-sm font-medium text-[#5A5A5A] mb-1" data-id="element-161">
            Total Revenue
          </p>
          <p className="text-3xl font-bold text-[#111111]" data-id="element-162">
            {formatPrice(stats.totalRevenue)}
          </p>
        </div>
        <div className="bg-white p-6 rounded-xl border border-[#E5E5E5] shadow-sm" data-id="element-163">
          <p className="text-sm font-medium text-[#5A5A5A] mb-1" data-id="element-164">
            Revenue Today
          </p>
          <p className="text-3xl font-bold text-[#111111]" data-id="element-165">
            {formatPrice(stats.revenueToday)}
          </p>
        </div>
        <div className="bg-white p-6 rounded-xl border border-[#E5E5E5] shadow-sm" data-id="element-166">
          <p className="text-sm font-medium text-[#5A5A5A] mb-1" data-id="element-167">
            Total Orders
          </p>
          <p className="text-3xl font-bold text-[#111111]" data-id="element-168">
            {stats.totalOrders}
          </p>
        </div>
        <div className="bg-white p-6 rounded-xl border border-[#E5E5E5] shadow-sm" data-id="element-169">
          <p className="text-sm font-medium text-[#5A5A5A] mb-1" data-id="element-170">
            Orders Today
          </p>
          <p className="text-3xl font-bold text-[#111111]" data-id="element-171">
            {stats.ordersToday}
          </p>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-10" data-id="element-172">
        <div className="bg-[#F0F4F8] p-4 rounded-lg border border-[#E5E5E5]" data-id="element-173">
          <p className="text-xs font-bold text-tealslate uppercase tracking-wider mb-1" data-id="element-174">
            Pending
          </p>
          <p className="text-xl font-bold text-[#111111]" data-id="element-175">{stats.pending}</p>
        </div>
        <div className="bg-[#F0F4F8] p-4 rounded-lg border border-[#E5E5E5]" data-id="element-176">
          <p className="text-xs font-bold text-tealslate uppercase tracking-wider mb-1" data-id="element-177">
            Shipped
          </p>
          <p className="text-xl font-bold text-[#111111]" data-id="element-178">{stats.shipped}</p>
        </div>
        <div className="bg-[#F0F4F8] p-4 rounded-lg border border-[#E5E5E5]" data-id="element-179">
          <p className="text-xs font-bold text-tealslate uppercase tracking-wider mb-1" data-id="element-180">
            Digital
          </p>
          <p className="text-xl font-bold text-[#111111]" data-id="element-181">{stats.digital}</p>
        </div>
        <div className="bg-[#F0F4F8] p-4 rounded-lg border border-[#E5E5E5]" data-id="element-182">
          <p className="text-xs font-bold text-tealslate uppercase tracking-wider mb-1" data-id="element-183">
            Physical
          </p>
          <p className="text-xl font-bold text-[#111111]" data-id="element-184">{stats.physical}</p>
        </div>
      </div>

      <div className="bg-white border border-[#E5E5E5] rounded-xl shadow-sm overflow-hidden" data-id="element-185">
        {/* Toolbar */}
        <div className="p-4 border-b border-[#E5E5E5] flex flex-col sm:flex-row gap-4 justify-between items-center bg-[#FAFAFA]" data-id="element-186">
          <div className="relative w-full sm:w-96" data-id="element-187">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#9A9A90]" data-id="element-188" />
            <input type="text" placeholder="Search orders, customers, emails..." value={search} onChange={e => setSearch(e.target.value)} className="w-full pl-10 pr-4 py-2 bg-white border border-[#E5E5E5] rounded-md text-sm focus:outline-none focus:border-tealslate focus:ring-1 focus:ring-tealslate" data-id="element-189" />
          </div>
          <div className="flex gap-3 w-full sm:w-auto" data-id="element-190">
            <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)} className="px-3 py-2 bg-white border border-[#E5E5E5] rounded-md text-sm text-[#111111] focus:outline-none focus:border-tealslate" data-id="element-191">
              <option value="all" data-id="element-192">All Statuses</option>
              <option value="pending" data-id="element-193">Pending</option>
              <option value="shipped" data-id="element-194">Shipped</option>
              <option value="delivered" data-id="element-195">Delivered</option>
              <option value="digital_delivered" data-id="element-196">Digital Delivered</option>
            </select>
            <select value={typeFilter} onChange={e => setTypeFilter(e.target.value)} className="px-3 py-2 bg-white border border-[#E5E5E5] rounded-md text-sm text-[#111111] focus:outline-none focus:border-tealslate" data-id="element-197">
              <option value="all" data-id="element-198">All Types</option>
              <option value="physical" data-id="element-199">Physical</option>
              <option value="digital" data-id="element-200">Digital</option>
            </select>
          </div>
        </div>

        {/* Table */}
        <div className="overflow-x-auto" data-id="element-201">
          <table className="w-full text-left border-collapse" data-id="element-202">
            <thead data-id="element-203">
              <tr className="bg-[#FAFAFA] border-b border-[#E5E5E5]" data-id="element-204">
                <th className="px-6 py-4 text-xs font-bold text-[#5A5A5A] uppercase tracking-wider" data-id="element-205">
                  Order
                </th>
                <th className="px-6 py-4 text-xs font-bold text-[#5A5A5A] uppercase tracking-wider cursor-pointer hover:text-[#111111]" onClick={() => handleSort('date')} data-id="element-206">
                  <div className="flex items-center gap-1" data-id="element-207">
                    Date <ArrowUpDown className="w-3 h-3" data-id="element-208" />
                  </div>
                </th>
                <th className="px-6 py-4 text-xs font-bold text-[#5A5A5A] uppercase tracking-wider" data-id="element-209">
                  Customer
                </th>
                <th className="px-6 py-4 text-xs font-bold text-[#5A5A5A] uppercase tracking-wider" data-id="element-210">
                  Status
                </th>
                <th className="px-6 py-4 text-xs font-bold text-[#5A5A5A] uppercase tracking-wider" data-id="element-211">
                  Total
                </th>
                <th className="px-6 py-4 text-xs font-bold text-[#5A5A5A] uppercase tracking-wider" data-id="element-212">
                  Payment
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[#E5E5E5]" data-id="element-213">
              {filteredOrders.length === 0 ? <tr data-id="element-214">
                  <td colSpan={6} className="px-6 py-12 text-center text-[#5A5A5A]" data-id="element-215">
                    No orders found matching your filters.
                  </td>
                </tr> : filteredOrders.map(order => <tr key={order.id} className="hover:bg-[#FAFAFA] transition-colors" data-id="element-216">
                    <td className="px-6 py-4" data-id="element-217">
                      <Link to={`/admin/orders/${order.id}`} className="text-tealslate font-bold hover:underline" data-id="element-218">
                        #{order.id.slice(0, 8).toUpperCase()}
                      </Link>
                    </td>
                    <td className="px-6 py-4 text-sm text-[#5A5A5A]" data-id="element-219">
                      {formatDate(order.createdAt)}
                    </td>
                    <td className="px-6 py-4" data-id="element-220">
                      <p className="text-sm font-medium text-[#111111]" data-id="element-221">
                        {order.shippingAddress?.recipientName || 'Digital Customer'}
                      </p>
                      <p className="text-xs text-[#5A5A5A]" data-id="element-222">{order.email}</p>
                    </td>
                    <td className="px-6 py-4" data-id="element-223">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium capitalize
                        ${order.fulfillmentStatus === 'pending' ? 'bg-amber-100 text-amber-800' : order.fulfillmentStatus === 'shipped' ? 'bg-blue-100 text-blue-800' : 'bg-green-100 text-green-800'}`} data-id="element-224">
                        {order.fulfillmentStatus.replace('_', ' ')}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-sm font-medium text-[#111111]" data-id="element-225">
                      {formatPrice(order.total)}
                    </td>
                    <td className="px-6 py-4" data-id="element-226">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium capitalize
                        ${order.status === 'paid' ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}`} data-id="element-227">
                        {order.status}
                      </span>
                    </td>
                  </tr>)}
            </tbody>
          </table>
        </div>
      </div>
    </div>;
}